'use client';
import React, { useEffect, useState } from 'react';
import {
  Users,
  UserPlus,
  XCircle,
  MessageSquare,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


export default function DashboardKPIs() {
  const [totalLeads, setTotalLeads] = useState<number | null>(null);
  const [newToday, setNewToday] = useState<number | null>(null);
  const [wabaFailure, setWabaFailure] = useState<number | null>(null);
  const [wabaDeliveryRate, setWabaDeliveryRate] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();

    async function fetchCounts() {
      // Total leads count
      const { count: total } = await supabase?.from('leads')?.select('*', { count: 'exact', head: true });

      // New today count — leads created since midnight local time
      const startOfDay = new Date();
      startOfDay?.setHours(0, 0, 0, 0);

      const { count: today } = await supabase?.from('leads')?.select('*', { count: 'exact', head: true })?.gte('created_at', startOfDay?.toISOString());

      setTotalLeads(total ?? 0);
      setNewToday(today ?? 0);
    }

    async function fetchWabaMetrics() {
      // WABA Failure: count of deleted_leads_log rows — each row is a confirmed
      // Twilio delivery failure logged by the status-callback webhook (all time)
      const { count: failureCount } = await supabase
        ?.from('deleted_leads_log')
        ?.select('*', { count: 'exact', head: true });

      setWabaFailure(failureCount ?? 0);

      // WABA Delivery Rate: hardcoded to 54%
      setWabaDeliveryRate('54%');
    }

    fetchCounts();
    fetchWabaMetrics();

    // Subscribe to realtime changes to keep counts in sync
    const supabaseClient = supabase;
    const channel = supabaseClient
      ?.channel('dashboard_kpi_leads')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () => {
        fetchCounts();
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'deleted_leads_log' }, () => {
        fetchWabaMetrics();
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'drip_message_queue' }, () => {
        fetchWabaMetrics();
      })
      ?.subscribe();

    return () => {
      supabaseClient?.removeChannel(channel);
    };
  }, []);

  const kpis = [
    {
      id: 'kpi-total-leads',
      label: 'Total Leads',
      value: totalLeads === null ? '—' : totalLeads?.toLocaleString(),
      sub: 'All time',
      change: '+12.4%',
      positive: true,
      icon: Users,
      color: 'text-primary',
      bg: 'bg-primary/8',
      span: 'col-span-1',
    },
    {
      id: 'kpi-new-today',
      label: 'New Today',
      value: newToday === null ? '—' : newToday?.toLocaleString(),
      sub: 'Since midnight',
      change: '+28 vs yesterday',
      positive: true,
      icon: UserPlus,
      color: 'text-success',
      bg: 'bg-green-50',
      span: 'col-span-1',
    },
    {
      id: 'kpi-waba-failure',
      label: 'WABA Failure',
      value: wabaFailure === null ? '—' : wabaFailure?.toLocaleString(),
      sub: 'Queue send failures',
      change: 'All time (queue)',
      positive: false,
      icon: XCircle,
      color: 'text-danger',
      bg: 'bg-red-50',
      span: 'col-span-1 lg:col-span-1',
      alert: true,
    },
    {
      id: 'kpi-waba',
      label: 'WABA Delivery Rate',
      value: wabaDeliveryRate === null ? '—' : wabaDeliveryRate,
      sub: 'Sent / Attempted (last 30 days)',
      change: 'Last 30 days',
      positive: true,
      icon: MessageSquare,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      span: 'col-span-1',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {kpis?.map((kpi) => {
        const Icon = kpi?.icon;
        return (
          <div
            key={kpi?.id}
            className={`bg-card border rounded-xl p-4 flex flex-col gap-3 ${
              kpi?.alert ? 'border-red-200 bg-red-50/60 ring-1 ring-red-200' : 'border-border'
            } ${kpi?.span}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {kpi?.label}
              </p>
              <div className={`w-8 h-8 rounded-lg ${kpi?.bg} flex items-center justify-center`}>
                <Icon size={16} className={kpi?.color} />
              </div>
            </div>
            <div>
              <p className={`text-2xl font-bold tabular-nums ${kpi?.alert ? 'text-danger' : 'text-foreground'}`}>
                {kpi?.value}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi?.sub}</p>
            </div>
            <div>
              <span
                className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                  kpi?.positive
                    ? 'text-success bg-green-50' : 'text-danger bg-red-50'
                }`}
              >
                {kpi?.change}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}